import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight } from "lucide-react";
import { CustomHeader } from "../components/AuthHeader";
import { useNotificationStore } from "../features/notifications/notificationStore";
import { formatDateTime } from "../utils/orderFormat";

export default function NotificationsScreen() {
  const navigate = useNavigate();
  const { notifications, isLoading, error, fetchNotifications, markAllRead } =
    useNotificationStore();

  useEffect(() => {
    // Mark as read after loading, so this visit still shows which ones are new
    fetchNotifications().then(() => markAllRead());
  }, [fetchNotifications, markAllRead]);

  return (
    <div className="bg-gray-50 pb-24 md:bg-transparent md:pb-0 md:max-w-3xl md:mx-auto">
      <div className="bg-white px-6 pt-5 pb-5 shadow-sm rounded-b-3xl md:rounded-3xl mb-5">
        <CustomHeader title="Notifications" subtitle="Updates on your orders" />
      </div>

      <div className="px-5 md:px-0">
        {isLoading && notifications.length === 0 && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#44B6CE] border-t-transparent animate-spin rounded-full" />
          </div>
        )}

        {error && <p className="text-sm text-red-500 text-center py-6">{error}</p>}

        {!isLoading && !error && notifications.length === 0 && (
          <div className="flex flex-col items-center py-20 gap-3">
            <div className="bg-[#44B6CE]/10 rounded-full p-6">
              <Bell className="h-8 w-8 text-[#44B6CE]" />
            </div>
            <p className="text-sm text-gray-500">No notifications yet.</p>
          </div>
        )}

        {notifications.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => n.order_id && navigate(`/order/details?order=${n.order_id}`)}
                className="w-full text-left px-4 py-3.5 flex items-start gap-3 active:bg-gray-50"
              >
                <span
                  className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                    n.is_read ? "bg-transparent" : "bg-[#44B6CE]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <p className="text-sm font-bold text-gray-900">{n.title}</p>
                    <p className="text-[11px] text-gray-400 whitespace-nowrap">
                      {formatDateTime(n.createdAt)}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">{n.content}</p>
                  {n.order_no && (
                    <p className="text-xs text-gray-400 mt-1">Order #{n.order_no}</p>
                  )}
                </div>
                {n.order_id && <ChevronRight size={16} className="text-gray-300 mt-1" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
